import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import {
  Device,
  FetchStreamsAPIResponse,
  Filters,
  Key,
  SearchDeviceResponse,
  SearchDevicesFiltersType,
} from "../../../../BytebeamClient";
import {
  ClickhouseToTSTypes,
  User,
  getTypeFromClickhouseToTS,
} from "../../../../util";
import { beamtoast } from "../../../common/CustomToast";
import { ErrorMessage } from "../../../common/ErrorMessage";
import LoadingAnimation from "../../../common/Loader";
import { ThinDivider } from "../../Dashboards/Panel/util";
import DevicesFilterUI, {
  FilterState,
} from "../../DeviceManagement/Devices/DevicesFilterUI/DevicesFilterUI";
import { validateTimestampInterval } from "../../util";
import ActionDisplaySection from "./ActionDisplaySection";
import AdvancedPhasedSection from "./AdvancedPhasedSection";
import DeviceSelectionSection from "./DeviceSelectionSection";
import { PhaseData } from "./NewAction";

export const PhaseSectionContainer = styled.div`
  width: 100%;
  position: relative;
`;

export type PhaseErrorsMessage = { [key: string]: string | null };

type PhaseContentProps = {
  phase: PhaseData;
  phasesData: PhaseData[];
  setPhasesData: (phasesData: PhaseData[]) => void;
  activePhase: string;
  phaseErrorsMessage: PhaseErrorsMessage;
  setPhaseErrorsMessage: (
    error: (prevErrors: PhaseErrorsMessage) => PhaseErrorsMessage
  ) => void;
  setFilters: (arg0: Filters) => void;
  debouncedUpdateFilters: any;
  devices: SearchDeviceResponse;
  devicesLoading: boolean;
  setDevicesLoading: (arg0: boolean) => void;
  allSelected: boolean;
  setAllSelected: (allSelected: boolean) => void;
  selectedDevices: SearchDeviceResponse;
  setSelectedDevices: (selectedDevices: SearchDeviceResponse) => void;
  page: number;
  allowedFilterBys: Key[];
  pageLimit: number;
  onPageChange: (e, { activePage }) => void;
  metadataKeysToShow: string[];
  setMetadataKeysToShow: React.Dispatch<React.SetStateAction<string[]>>;
  isPhasedRollout: boolean;
  user: User;
  stateKeys: string[];
  metadataKeys: string[];
  detailedStreamsList: FetchStreamsAPIResponse;
};

const PhaseContent = ({
  phase,
  phasesData,
  setPhasesData,
  activePhase,
  phaseErrorsMessage,
  setPhaseErrorsMessage,
  setFilters,
  debouncedUpdateFilters,
  devices,
  devicesLoading,
  setDevicesLoading,
  allSelected,
  setAllSelected,
  selectedDevices,
  setSelectedDevices,
  page,
  pageLimit,
  onPageChange,
  allowedFilterBys,
  metadataKeysToShow,
  setMetadataKeysToShow,
  isPhasedRollout,
  user,
  stateKeys,
  metadataKeys,
  detailedStreamsList,
}: PhaseContentProps) => {
  const history = useHistory();
  const location = useLocation();
  const [showActionDisplayDropdown, setShowActionDisplayDropdown] =
    useState<boolean>(false);
  const [devicesFilterQuery, setDevicesFilterQuery] =
    useState<SearchDevicesFiltersType>({});

  const getNumericalDeviceIDs = (devices: Device[]) =>
    devices.map((device) => device.id);

  const getDeviceShadowFieldType = (field: string): ClickhouseToTSTypes => {
    if (field === "id") {
      return "int";
    }
    const columnMeta = detailedStreamsList["device_shadow"]?.fields?.[field];
    const columnType = columnMeta?.type || "string";
    return getTypeFromClickhouseToTS(columnType);
  };

  const generateFilterId = (field: string): string => {
    let hash = 0;
    const str = `metadata:${field}`;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash + char) | 0;
    }
    return Math.abs(hash).toString(36);
  };

  const convertFiltersToFilterStateArray = (
    filters: Filters
  ): FilterState[] => {
    if (!filters || Object.keys(filters).length === 0) {
      return [];
    }

    const filterStates: FilterState[] = [];
    // Sort keys to ensure consistent ordering for comparison
    Object.keys(filters)
      .sort()
      .forEach((field) => {
        const values = filters[field];
        if (values && Array.isArray(values) && values.length > 0) {
          // Filter out null values and convert to string array
          // Sort values to ensure consistent ordering
          const stringValues = values
            .filter((v) => v !== null && v !== undefined)
            .map((v) => String(v))
            .sort() as string[];
          if (stringValues.length > 0) {
            filterStates.push({
              id: generateFilterId(field),
              field: field,
              columnType: getDeviceShadowFieldType(field),
              type: "metadata",
              operator: "=",
              isNullOperator: false,
              value: stringValues,
            });
          }
        }
      });

    return filterStates;
  };

  const updateUrlWithFilters = (filtersToUpdate: FilterState[]): void => {
    const searchParams = new URLSearchParams(location.search);
    const currentFiltersStr = searchParams.get("filters");

    const newFiltersStr =
      filtersToUpdate.length > 0 ? JSON.stringify(filtersToUpdate) : "";

    if (currentFiltersStr !== newFiltersStr) {
      if (filtersToUpdate.length > 0) {
        searchParams.set("filters", newFiltersStr);
      } else {
        searchParams.delete("filters");
      }

      history.replace({
        pathname: location.pathname,
        search: searchParams.toString(),
      });
    }
  };

  const updateActivePhaseTimestamp = (
    time: number | Date,
    activePhase: string
  ) => {
    // Validate if the timestamp has a 5-minute interval
    const isValidTimestamp = validateTimestampInterval(
      time,
      activePhase,
      phasesData
    );

    if (
      !isValidTimestamp &&
      activePhase !== "Phase I" &&
      phasesData?.length > 1
    ) {
      const errorMsg =
        "Please ensure a minimum interval of 5 minutes from the previous phase with timestamp.";
      setPhaseErrorsMessage((prevErrors) => ({
        ...prevErrors,
        [activePhase]: errorMsg,
      }));
    } else {
      setPhaseErrorsMessage((prevErrors) => ({
        ...prevErrors,
        [activePhase]: null,
      }));
    }

    const newPhasesData = phasesData.map((phase) => {
      if (phase.name === activePhase) {
        phase.trigger_on = {
          timestamp: moment(time).toDate(),
          ...(phase.trigger_on.hasOwnProperty("failure_percentage")
            ? { failure_percentage: phase.trigger_on.failure_percentage }
            : {}),
        };
      }
      return phase;
    });
    setPhasesData(newPhasesData);
  };

  const updateActivePhaseFraction = (value: number, activePhase: string) => {
    const newPhasesData = phasesData.map((phase) => {
      if (phase.name === activePhase) {
        phase.info.fraction = parseInt(value.toString());
      }
      return phase;
    });
    setPhasesData(newPhasesData);
  };

  const updateSuccessPercentage = (value: number, activePhase: string) => {
    if (value < 0 || value > 100) {
      beamtoast.error("Success rate should be between 0 and 100");
      return;
    }

    const newPhasesData = phasesData.map((phase) => {
      if (phase.name === activePhase) {
        phase.trigger_on = {
          success_percentage: Number(value),
          ...(phase.trigger_on.failure_percentage
            ? { failure_percentage: phase.trigger_on.failure_percentage }
            : {}),
        };
      }
      return phase;
    });

    setPhasesData(newPhasesData);
  };

  const updateFailurePercentage = (
    value: number,
    phaseId: number,
    abortPhase: boolean
  ) => {
    if (value <= 0 || value > 100) {
      beamtoast.error("Failure percentage should be between 1 and 100");
      return;
    }

    const newPhasesData = phasesData.map((phase) => {
      if (phase.id === phaseId) {
        if (abortPhase) {
          phase.trigger_on.failure_percentage = Number(value);
        } else {
          delete phase.trigger_on.failure_percentage;
        }
      }
      return phase;
    });

    setPhasesData(newPhasesData);
  };

  const updateFixedListPhaseData = (updatedDevices: SearchDeviceResponse) => {
    let updatedPhaseData: PhaseData = { ...phasesData[phase.id - 1] };
    updatedPhaseData.info.device_ids = getNumericalDeviceIDs(
      updatedDevices.devices
    );
    const metadataFilters = devicesFilterQuery.metaDatafilters || {};
    updatedPhaseData.info.filter = metadataFilters as Record<string, string[]>;
    updatedPhaseData.info.fraction = 100;
    if (updatedDevices.devices?.length === 0) {
      const hasFilters =
        Object.keys(metadataFilters)?.length > 0 ||
        (devicesFilterQuery.stateFilters?.length ?? 0) > 0;
      updatedPhaseData.info.type = hasFilters
        ? "filter-fraction"
        : "fixed-list";
    } else {
      updatedPhaseData.info.type = "fixed-list";
    }
    if ((devicesFilterQuery.stateFilters?.length ?? 0) > 0) {
      updatedPhaseData.info.state_filter = devicesFilterQuery.stateFilters;
    }

    let updatedPhasesData: PhaseData[] = [...phasesData];
    updatedPhasesData[phase.id - 1] = updatedPhaseData;
    setPhasesData(updatedPhasesData);
  };

  const selectDevice = (device: Device) => {
    let updatedDevices: SearchDeviceResponse = {
      devices: [...selectedDevices.devices, device],
      count: selectedDevices.count + 1,
    };
    setSelectedDevices(updatedDevices);

    updateFixedListPhaseData(updatedDevices);
  };

  const clearDevice = (device: Device) => {
    const filteredDevices = selectedDevices.devices.filter(
      (selectedDevice) => selectedDevice.id !== device.id
    );

    const updatedDevices: SearchDeviceResponse = {
      devices: filteredDevices,
      count: selectedDevices.count - 1,
    };
    setSelectedDevices(updatedDevices);

    updateFixedListPhaseData(updatedDevices);
  };

  const handleAllDevicesSelect = async (selectAll: boolean) => {
    setAllSelected(selectAll);

    let updatedPhaseData: PhaseData = { ...phasesData[phase.id - 1] };
    const metadataFilters = devicesFilterQuery.metaDatafilters || {};
    const hasFilters =
      Object.keys(metadataFilters)?.length > 0 ||
      (devicesFilterQuery.stateFilters?.length ?? 0) > 0;
    if (!hasFilters && !selectAll) {
      updatedPhaseData.info = {
        type: "fixed-list",
        device_ids: [],
      };
    } else {
      updatedPhaseData.info = {
        type: "filter-fraction",
        filter: metadataFilters as Record<string, string[]>,
        fraction: 100,
      };
      if ((devicesFilterQuery.stateFilters?.length ?? 0) > 0) {
        updatedPhaseData.info.state_filter = devicesFilterQuery.stateFilters;
      }
    }
    let updatedPhasesData: PhaseData[] = [...phasesData];
    updatedPhasesData[phase.id - 1] = updatedPhaseData;
    setPhasesData(updatedPhasesData);
  };

  const onFilterChange = useCallback(
    (searchFilters: SearchDevicesFiltersType) => {
      setDevicesLoading(true);
      setDevicesFilterQuery(searchFilters);

      const metadataFilters = searchFilters.metaDatafilters || {};
      const hasFilters =
        Object.keys(metadataFilters)?.length > 0 ||
        (searchFilters.stateFilters?.length ?? 0) > 0;

      if (!hasFilters) {
        setAllSelected(false);
        setFilters({});
      } else {
        const filtersForState: Filters = {};
        Object.keys(metadataFilters).forEach((key) => {
          const value = metadataFilters[key];
          if (Array.isArray(value)) {
            filtersForState[key] = value.filter((v) => v !== null) as string[];
          } else if (value !== null && value !== "") {
            filtersForState[key] = [value as string];
          }
        });
        setAllSelected(true);
        setFilters(filtersForState);
      }

      debouncedUpdateFilters(searchFilters, allowedFilterBys);
    },
    [
      allowedFilterBys,
      setDevicesLoading,
      debouncedUpdateFilters,
      setAllSelected,
      setFilters,
    ]
  );

  useEffect(() => {
    if (phase.name === activePhase) {
      const metadataFilters = phase.info.filter || {};
      const stateFilters = (phase.info as any).state_filter || [];
      const newQuery: SearchDevicesFiltersType = {
        ...(Object.keys(metadataFilters).length > 0
          ? { metaDatafilters: metadataFilters }
          : {}),
        ...(stateFilters.length > 0 ? { stateFilters } : {}),
      };
      setDevicesFilterQuery(newQuery);
      const filtersForState: Filters = {};
      Object.keys(metadataFilters).forEach((key) => {
        const value = metadataFilters[key];
        if (Array.isArray(value)) {
          filtersForState[key] = value.filter((v) => v !== null) as string[];
        } else if (value !== null) {
          filtersForState[key] = [value as string];
        }
      });
      setFilters(filtersForState);
      const hasFilters =
        Object.keys(metadataFilters).length > 0 || stateFilters.length > 0;
      setAllSelected(hasFilters);
      const searchParams = new URLSearchParams(location.search);
      const currentFiltersStr = searchParams.get("filters");
      let currentFilterStates: FilterState[] = [];
      if (currentFiltersStr) {
        try {
          currentFilterStates = JSON.parse(currentFiltersStr);
        } catch (error) {
          console.error("Error parsing current filters from URL:", error);
        }
      }

      const metadataFilterStates =
        convertFiltersToFilterStateArray(filtersForState);

      const stateFilterStates = currentFilterStates.filter(
        (f) => f.type === "state"
      );
      const combinedFilterStates = [
        ...metadataFilterStates,
        ...stateFilterStates,
      ];
      updateUrlWithFilters(combinedFilterStates);
    }
  }, [activePhase, phase.info.filter]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PhaseSectionContainer
      id={`phaseContent-${phasesData?.length + 2 - phase.id}`}
      style={{ display: activePhase === phase.name ? "block" : "none" }}
    >
      <div
        style={{
          display: "flex",
          gap: "16px",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <DevicesFilterUI
          user={user}
          stateKeys={stateKeys}
          metadataKeys={metadataKeys}
          detailedStreamsList={detailedStreamsList}
          onSearch={onFilterChange}
          devicesLoading={devicesLoading}
          showRefreshButton={
            (devicesFilterQuery?.stateFilters?.length ?? 0) > 0
          }
          refreshDevices={() => {
            onFilterChange(devicesFilterQuery);
          }}
          panelPosition="left"
        />

        {!devicesLoading && devices?.devices?.length !== 0 && (
          <ActionDisplaySection
            showActionDisplayDropdown={showActionDisplayDropdown}
            setShowActionDisplayDropdown={setShowActionDisplayDropdown}
            setMetadataKeysToShow={setMetadataKeysToShow}
            metadataKeysToShow={metadataKeysToShow}
            allowedMetadataKeys={allowedFilterBys}
            displayButtonStyle={{ display: "flex" }}
            panelPosition="left"
          />
        )}
      </div>

      {devicesLoading && (
        <LoadingAnimation
          loaderContainerHeight="350px"
          loadingText="Loading devices"
          fontSize="20px"
          loaderSize="48px"
        />
      )}
      {!devicesLoading && devices?.devices?.length !== 0 && (
        <>
          <DeviceSelectionSection
            devices={devices}
            allSelected={allSelected}
            setAllSelected={handleAllDevicesSelect}
            selectedDevices={selectedDevices}
            setSelectedDevices={setSelectedDevices}
            selectDevice={selectDevice}
            clearDevice={clearDevice}
            page={page}
            pageLimit={pageLimit}
            onPageChange={onPageChange}
            metadataKeysToShow={metadataKeysToShow}
            phase={activePhase}
          />
          <ThinDivider />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              margin: "42px 0px 36px 0px",
            }}
          >
            <AdvancedPhasedSection
              key={activePhase}
              isPhasedRollout={isPhasedRollout}
              phases={phasesData}
              activePhase={activePhase}
              setPhaseErrorsMessage={setPhaseErrorsMessage}
              phaseErrorsMessage={phaseErrorsMessage}
              updateActivePhaseTimestamp={updateActivePhaseTimestamp}
              updateActivePhaseFraction={updateActivePhaseFraction}
              updateFailurePercentage={updateFailurePercentage}
              updateSuccessPercentage={updateSuccessPercentage}
            />
          </div>
        </>
      )}
      {!devicesLoading && devices?.devices?.length === 0 && (
        <div
          style={{
            display: "flex",
            width: "100%",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <ErrorMessage
            marginTop="30px"
            marginBottom="30px"
            message={"No devices found!"}
          />
        </div>
      )}
    </PhaseSectionContainer>
  );
};

export default PhaseContent;
